import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Instagram, Facebook, MessageCircle } from "lucide-react";
import { api } from "../lib/api";

const SOCIALS = [
  { Icon: Instagram, id: "instagram", href: "#" },
  { Icon: Facebook, id: "facebook", href: "#" },
  { Icon: MessageCircle, id: "whatsapp", href: `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}` },
];

const LEGAL = [
  ["terms", "Terms of Service"],
  ["privacy", "Privacy Policy"],
  ["shipping-returns", "Shipping & Returns"],
  ["authenticity", "Authenticity"],
];

const linkCls = "hover:text-gold-light hover:pl-2 transition-[color,padding] duration-300";

export default function Footer() {
  const ref = useRef(null);
  const [site, setSite] = useState({});
  useEffect(() => {
    api.get("/settings").then((r) => setSite(r.data || {})).catch(() => {});
  }, []);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 50, damping: 20 });
  const y = useSpring(my, { stiffness: 50, damping: 20 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end end"] });
  const rotateX = useTransform(scrollYProgress, [0, 1], [55, 0]);
  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

  const onMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 50);
    my.set((e.clientY / window.innerHeight - 0.5) * 24);
  };

  return (
    <footer
      ref={ref}
      data-testid="site-footer"
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative bg-obsidian text-ivory overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold-light/60 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(212,175,55,0.14),transparent_60%)] pointer-events-none" />
      <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 pt-20 md:pt-28 pb-10">
        <div className="grid md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-4">
            <img src="/brand/logo-seal.png" alt="ATW seal" className="w-20 h-20 mb-6 drop-shadow-[0_0_28px_rgba(212,175,55,0.55)]" />
            <p className="text-[10px] tracking-[0.5em] uppercase gold-text mb-5">The House of Around The World · 
              A Creation of Around The World Ltd
            </p>
            <p className="text-ivory/50 text-sm leading-relaxed max-w-sm font-light">
              Founded by Dewan Ali. House fragrances, wrist watches, tailored trousers, shirts, polos, belts and
              candles — designed under one name and delivered to every corner of the world.
            </p>
            {(site.contact_email || site.contact_phone) && (
              <p data-testid="footer-contact" className="mt-5 text-ivory/60 text-sm">
                {site.contact_email && <a href={`mailto:${site.contact_email}`} className="hover:text-gold-light transition-colors">{site.contact_email}</a>}
                {site.contact_phone && <span className="block mt-1">{site.contact_phone}</span>}
              </p>
            )}
            <div className="flex gap-3 mt-8">
              {SOCIALS.map(({ Icon, id, href }) => (
                <motion.a
                  key={id}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${id}`}
                  aria-label={id}
                  whileHover={{ y: -4, rotateY: 18 }}
                  style={{ transformPerspective: 400 }}
                  className="p-2.5 border border-ivory/15 text-ivory/50 hover:text-gold-light hover:border-gold-light/60 transition-colors duration-300"
                >
                  <Icon size={16} strokeWidth={1.5} />
                </motion.a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 md:col-start-6">
            <h3 className="text-[10px] tracking-[0.35em] uppercase text-ivory/40 mb-6">Shop</h3>
            <ul className="space-y-3.5 text-sm text-ivory/65 font-light">
              {["Perfumes", "Watches", "Shirts", "Trousers", "Polos", "Belts", "Candles"].map((c) => (
                <li key={c}>
                  <Link to={`/shop?cat=${c}`} data-testid={`footer-link-${c.toLowerCase()}`} className={linkCls}>
                    {c}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-[10px] tracking-[0.35em] uppercase text-ivory/40 mb-6">House</h3>
            <ul className="space-y-3.5 text-sm text-ivory/65 font-light">
              <li><Link to="/" data-testid="footer-link-home" className={linkCls}>Home</Link></li>
              <li><Link to="/about" data-testid="footer-link-about" className={linkCls}>About Dewan Ali</Link></li>
              <li><Link to="/lookbook" data-testid="footer-link-lookbook" className={linkCls}>Lookbook</Link></li>
              <li><a href="/#manifesto" data-testid="footer-link-manifesto" className={linkCls}>Manifesto</a></li>
              <li><Link to="/account" data-testid="footer-link-account" className={linkCls}>My Account</Link></li>
              <li><Link to="/track" data-testid="footer-link-track" className={linkCls}>Track Order</Link></li>
              <li><Link to="/shop" data-testid="footer-link-shop" className={linkCls}>All Products</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h3 className="text-[10px] tracking-[0.35em] uppercase text-ivory/40 mb-6">Legal & Care</h3>
            <ul className="space-y-3.5 text-sm text-ivory/65 font-light">
              {LEGAL.map(([slug, label]) => (
                <li key={slug}>
                  <Link to={`/legal/${slug}`} data-testid={`footer-legal-${slug}`} className={linkCls}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
            <a
              href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-concierge"
              className="mt-8 inline-flex items-center gap-2.5 border border-gold/50 text-gold-light px-5 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-gold-light hover:text-ink transition-colors"
            >
              <MessageCircle size={14} /> Private concierge
            </a>
          </div>
        </div>

        <div className="mt-20 md:mt-28 select-none" aria-hidden="true" style={{ perspective: 900 }}>
          <motion.p
            style={{ x, y, rotateX, opacity, transformOrigin: "bottom center" }}
            className="font-serif uppercase gold-text text-[13.5vw] md:text-[10.5vw] leading-[0.85] tracking-tight text-center whitespace-nowrap drop-shadow-[0_10px_40px_rgba(212,175,55,0.25)]"
          >
            Around The World
          </motion.p>
        </div>

        <div className="mt-12 pt-8 border-t border-ivory/10 flex flex-col md:flex-row items-center justify-between gap-4 text-[10px] tracking-[0.25em] uppercase text-ivory/35">
          <p>© {new Date().getFullYear()} ATW · Around The World Ltd · Founded by Dewan Ali · All rights reserved</p>
          <p className="text-gold-light/60">Prices settled in GBP · Worldwide delivery</p>
        </div>
      </div>
    </footer>
  );
}
